import CallEndIcon from "@mui/icons-material/CallEnd"
import CallMadeIcon from "@mui/icons-material/CallMade"
import CallReceivedIcon from "@mui/icons-material/CallReceived"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import DialpadIcon from "@mui/icons-material/Dialpad"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import GroupsIcon from "@mui/icons-material/Groups"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import MicNoneIcon from "@mui/icons-material/MicNone"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded"
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed"
import RingVolumeIcon from "@mui/icons-material/RingVolume"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser"
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import dayjs from "dayjs"
import { useMemo, useState } from "react"
import { CallAdminLog } from "__generated__/graphql"
import {
  buildCallTimeline,
  CallTimelineEvent,
  CallTimelineIcon,
  CallTimelineTone
} from "./callRunTimeline.utils"

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"

const TONE_COLORS: Record<CallTimelineTone, string> = {
  neutral: "text.disabled",
  info: "info.main",
  good: "success.main",
  warn: "warning.main",
  bad: "error.main"
}

const ICONS: Record<CallTimelineIcon, typeof CallEndIcon> = {
  inbound: CallReceivedIcon,
  outbound: CallMadeIcon,
  team: GroupsIcon,
  gate: VerifiedUserIcon,
  bot: SmartToyIcon,
  ivr: DialpadIcon,
  queue: HourglassEmptyIcon,
  dial: RingVolumeIcon,
  answered: CheckCircleIcon,
  declined: PhoneMissedIcon,
  missed: PhoneMissedIcon,
  transfer: SwapHorizIcon,
  survey: StarBorderIcon,
  record: MicNoneIcon,
  hangup: CallEndIcon,
  end: PhoneForwardedIcon,
  error: ErrorOutlineIcon
}

const KIND_LABELS: Record<string, string> = {
  inbound: "Gelen çağrı",
  outbound: "Giden çağrı",
  team: "Ekip araması",
  unknown: "Çağrı"
}

function formatOffset(offsetMs: number): string {
  if (offsetMs < 1000) return "0s"
  const seconds = offsetMs / 1000
  if (seconds < 60) return `+${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  return `+${minutes}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0")}`
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textTransform: "uppercase", fontSize: 9.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: MONO }}>
        {value}
      </Typography>
    </Box>
  )
}

function EventRow({ event }: { event: CallTimelineEvent }) {
  const Icon = ICONS[event.icon]
  const color = TONE_COLORS[event.tone]

  return (
    <Stack direction="row" spacing={1.25} sx={{ minWidth: 0, py: 0.4 }}>
      <Typography
        variant="caption"
        sx={{
          fontFamily: MONO,
          color: "text.disabled",
          width: 46,
          flexShrink: 0,
          textAlign: "right",
          lineHeight: 1.9
        }}
      >
        {formatOffset(event.offsetMs)}
      </Typography>
      <Icon sx={{ fontSize: 16, color, flexShrink: 0, mt: 0.35 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
          {event.label}
        </Typography>
        {event.detail && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", overflowWrap: "anywhere" }}
          >
            {event.detail}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}

/**
 * The recordings the call produced, playable in place. `preload="none"` keeps opening a
 * drawer from pulling every wav off S3 — the browser only fetches once play is pressed.
 */
function RecordingPlayers({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null

  return (
    <Box sx={{ mt: 1.25 }}>
      {urls.map((url, index) => (
        <Stack
          key={url}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: index === 0 ? 0 : 0.75, minWidth: 0 }}
        >
          <MicNoneIcon
            sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }}
          />
          {urls.length > 1 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0, fontFamily: MONO }}
            >
              #{index + 1}
            </Typography>
          )}
          <Box
            component="audio"
            src={url}
            controls
            preload="none"
            sx={{ flex: 1, minWidth: 0, height: 32 }}
          />
          <Tooltip title="Kaydı yeni sekmede aç / indir">
            <IconButton
              size="small"
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
    </Box>
  )
}

/**
 * The narrated view of a call: one glance answers who called whom, which gates and bots it
 * passed, every agent that was dialed and how each responded, and how it ended. Reading the
 * raw step groups below should be the exception, not the default.
 */
export default function CallRunTimeline({
  log,
  agentNames
}: {
  log: CallAdminLog | null | undefined
  agentNames: Map<string, string>
}) {
  const [open, setOpen] = useState(true)

  const timeline = useMemo(
    () => (log ? buildCallTimeline({ log, agentNames }) : null),
    [log, agentNames]
  )

  if (!timeline || timeline.events.length === 0) return null

  const { summary, events } = timeline
  const startedAt = events[0]?.at

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        mb: 2,
        bgcolor: "background.paper"
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" }
        }}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
        >
          {KIND_LABELS[summary.kind] ?? KIND_LABELS.unknown} · Akış özeti
        </Typography>
        {summary.failedCount > 0 && (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`${summary.failedCount} hata`}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        {summary.outcome && (
          <Chip
            size="small"
            label={summary.outcome.label}
            color={
              summary.outcome.tone === "good"
                ? "success"
                : summary.outcome.tone === "bad"
                  ? "error"
                  : summary.outcome.tone === "warn"
                    ? "warning"
                    : "default"
            }
            sx={{ height: 22, flexShrink: 0 }}
          />
        )}
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: "text.disabled",
            flexShrink: 0,
            transform: open ? "none" : "rotate(-90deg)",
            transition: "transform 120ms"
          }}
        />
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}
      >
        {summary.fromLabel || "?"}
        {summary.personName ? ` (${summary.personName})` : ""} →{" "}
        {summary.toLabel || "?"}
        {startedAt ? ` · ${dayjs(startedAt).format("DD MMM HH:mm:ss")}` : ""}
      </Typography>

      <Stack direction="row" spacing={2.5} sx={{ mt: 1, flexWrap: "wrap" }}>
        <StatChip
          label="Toplam"
          value={
            summary.totalDurationSec !== undefined
              ? `${summary.totalDurationSec} sn`
              : "-"
          }
        />
        <StatChip
          label="Konuşma"
          value={summary.talkSec !== undefined ? `${summary.talkSec} sn` : "-"}
        />
        <StatChip
          label="Kuyruk"
          value={
            summary.queueSec !== undefined ? `${summary.queueSec} sn` : "-"
          }
        />
        <StatChip label="Aranan agent" value={String(summary.agentsTried)} />
        <StatChip label="Anket" value={summary.surveyScore ?? "-"} />
        <StatChip
          label="Kayıt"
          value={
            summary.hasRecording
              ? summary.hasTranscript
                ? "var + metin"
                : "var"
              : "-"
          }
        />
      </Stack>

      <RecordingPlayers urls={summary.recordingUrls} />

      <Collapse in={open} timeout={120} unmountOnExit>
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: 1, borderColor: "divider" }}>
          {events.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}
